// src/components/ClientApp/ClientApp.jsx
import React, { useEffect } from "react";
import { Routes, Route, useParams, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClientConfig,
  selectConfigLoading,
  selectConfigError,
  selectClientSlug,
  selectFeatures,
  clearConfig,
} from "../../store/configSlice";
import { clearData } from "../../store/dataSlice";
import { resetMapState } from "../../store/mapSlice";
import LoadingSpinner from "../UI/LoadingSpinner";
import MapView from "../MapView/MapView";
import ReportsView from "../Reports/ReportsView";
import FaqView from "../Faq/FaqView";

/**
 * ClientApp component
 * Main container for client-specific views
 */
const ClientApp = () => {
  const dispatch = useDispatch();
  const { clientSlug } = useParams();

  const loading = useSelector(selectConfigLoading);
  const error = useSelector(selectConfigError);
  const loadedClientSlug = useSelector(selectClientSlug);
  const features = useSelector(selectFeatures);

  const reportsEnabled = features?.reports?.enabled || false;

  // Load client config when slug changes
  useEffect(() => {
    if (clientSlug && clientSlug !== loadedClientSlug) {
      // Clear previous client's data
      dispatch(clearConfig());
      dispatch(clearData());
      dispatch(resetMapState());

      // Load new client config
      dispatch(fetchClientConfig(clientSlug));
    }
  }, [clientSlug, loadedClientSlug, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearConfig());
      dispatch(clearData());
      dispatch(resetMapState());
    };
  }, [dispatch]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner
          size="large"
          message="Loading client configuration..."
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Error Loading Client
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => dispatch(fetchClientConfig(clientSlug))}
            className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Config not loaded yet
  if (!loadedClientSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="large" message="Initializing..." />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<MapView />} />
      <Route
        path="/reports"
        element={
          reportsEnabled ? (
            <ReportsView />
          ) : (
            <Navigate to={`/${clientSlug}`} replace />
          )
        }
      />
      <Route path="/faq" element={<FaqView />} />
    </Routes>
  );
};

export default ClientApp;
